import * as Option from 'fp-ts/Option';
import * as ArrayFP from 'fp-ts/Array';
import { pipe } from 'fp-ts/pipeable';
/**
 * List of language codes for URL path
 */
export const defaultLangPaths = [
    'en',
    'nl',
    'de',
    'fr',
    'it',
    'es',
    'zh-Hans',
    'zh-Hant',
    'pt',
    'da',
    'sv',
    'no',
    'pl',
    'el',
    'hu',
    'ro',
    'fi',
];
/**
 * List of regional codes in URL
 */
export const defaultLangDomains = [
    'com',
    'nl',
    'de',
    'eu',
    'fr',
    'be',
    'it',
    'es',
    'cn',
    'hk',
    'pt',
    'dk',
    'se',
    'no',
    'pl',
    'gr',
    'hu',
    'ro',
    'fi',
];
const getLangPathRegx = (langPaths) => new RegExp(`(${langPaths.map((lp) => `/${lp}/`).join('|')})`);
const getLangDomainRegx = (langDomains) => new RegExp(`(${langDomains.map((ld) => `\\.${ld}`).join('|')})`);
/**
 * adds missing slash
 * e.g. catawiki.com/hu -> catawiki.com/hu/
 *
 * Why slash should be added?
 * getLangPathRegx() works correct only if there is a trailing slash after the langPath
 * so uri 'catawiki.com/hu' is considered as langPath equals to empty string
 *
 * this method should be called before using getLangPathRegx()
 * @param url
 */
const addTrailingSlash = (url) => 
// split string by slash
// get last element
// check it's not empty
// check it's in defaultLangPaths
// return url with appended slash
// otherwise return unchanged url
pipe(url.split('/'), ArrayFP.last, Option.filter((chunk) => chunk.length > 0), Option.filter((lang) => defaultLangPaths.includes(lang)), Option.map(() => `${url}/`), Option.getOrElse(() => url));
/**
 * @example
 * import { LangUri } from '@catawiki/runtime-toolkit'
 *
 * const uri = LangUri.LangUri.of(window.location.href) // https://catawiki.com/fr/user/account
 *
 * <button onClick={() => window.history.push(uri.getPath('seller/lots'))}>
 *   Move to offered lots
 * </button>
 */
export class LangUri {
    getUri;
    getDomain;
    getLangDomain;
    getLangPath;
    constructor(params) {
        this.getUri = () => params.uri;
        this.getDomain = () => params.domain;
        this.getLangDomain = () => params.langDomain;
        this.getLangPath = () => params.langPath;
    }
    /**
     * Method for creating URL wrapping entity
     * @example
     *
     * import { LangUri } from '@catawiki/runtime-toolkit'
     *
     * LangUri.LangUri.of("https://catawiki.com/da/seller/lots")
     */
    static of(url, supportedLangs) {
        const langPathRegx = getLangPathRegx(supportedLangs?.langPaths || defaultLangPaths);
        const langDomainRegx = getLangDomainRegx(supportedLangs?.langDomains || defaultLangDomains);
        const paramsSplit = url.split('?');
        const pathDomainSplit = addTrailingSlash(paramsSplit[0]).split(langDomainRegx);
        const domain = pathDomainSplit.length > 2
            ? `${pathDomainSplit[0]}${pathDomainSplit[1]}`
            : '';
        const path = pathDomainSplit[pathDomainSplit.length - 1];
        const pathLangSplit = path.split(langPathRegx);
        const langPath = !pathLangSplit[0] ? pathLangSplit[1] : '';
        return new LangUri({
            uri: `${domain}${langPath}`,
            domain,
            langDomain: pathDomainSplit[1] || '',
            langPath,
        });
    }
    /**
     * Overwrites native method for easier debugging, defining how object will be logged for i.e.
     */
    inspect() {
        return {
            uri: this.getUri(),
            domain: this.getDomain(),
            langDomain: this.getLangDomain(),
            langPath: this.getLangPath(),
        };
    }
    /**
     * Method for creating new paths based on saved url
     *
     * `path` - takes url pathname
     *
     * `absolute` - flags tells if you need output to be absolute URL, `false` by default
     *
     * @example
     *
     * import { LangUri } from '@catawiki/runtime-toolkit'
     *
     * const uri = LangUri.LangUri.of("https://catawiki.com/da/seller/lots")
     *
     * assert.equal(
     *  uri.generatePath('/l/123456?show_all=true'),
     *  "/da/l/123456?show_all=true"
     * )
     *
     * assert.equal(
     *  uri.generatePath('/l/123456?show_all=true', true),
     *  "https://catawiki.com/da/l/123456?show_all=true"
     * )
     */
    generatePath(path, absolute = false) {
        const normalizedPath = path.replace(/(\/)\1+/g, '/').replace(/^\//, '');
        if (absolute) {
            const uri = this.getUri();
            return uri.endsWith('/')
                ? uri + normalizedPath
                : `${uri}/${normalizedPath}`;
        }
        return (this.getLangPath() || '/') + normalizedPath;
    }
}
/**
 * Method to simply prepend a locale to a Path.
 * This is useful in SSR where generatePath is not able to work properly
 *
 * `locale` - The locale to prepend to the path
 * `path` - The path to prepend the locale to.
 */
export const generateLocalePath = (locale, path) => path.startsWith('/') ? `/${locale}${path}` : `/${locale}/${path}`;
